import React, { useState } from 'react';
import { List, ListItem, Typography, Box, Divider, Collapse, IconButton } from '@mui/material';
import { TOURNAMENT_RANKS } from './RankFilter';
import { styles } from './styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { styled } from '@mui/material/styles';

// Stylizowany komponent Collapse z płynniejszą animacją
const StyledCollapse = styled(Collapse)(({ theme }) => ({
  transition: theme.transitions.create('all', {
    duration: theme.transitions.duration.standard,
    easing: theme.transitions.easing.easeInOut,
  }),
  '& .MuiCollapse-wrapper': {
    transition: theme.transitions.create('all', {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut,
    }),
  },
  '& .MuiCollapse-wrapperInner': {
    transition: theme.transitions.create('all', {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut,
    }),
  }
}));

const MobileEventsList = ({ events, onEventClick }) => {
  const [showPastEvents, setShowPastEvents] = useState(false);
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0); // Ustawiamy godzinę na początek dnia

  // Sortowanie wydarzeń chronologicznie
  const sortedEvents = [...events].sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );

  // Rozdzielenie wydarzeń na przeszłe i przyszłe
  const { pastEvents, upcomingEvents } = sortedEvents.reduce(
    (acc, event) => {
      const eventDate = new Date(event.date);
      eventDate.setHours(0, 0, 0, 0); // Ustawiamy godzinę na początek dnia
      if (eventDate < currentDate) {
        acc.pastEvents.push(event);
      } else {
        acc.upcomingEvents.push(event);
      }
      return acc;
    },
    { pastEvents: [], upcomingEvents: [] }
  );

  // Grupowanie wydarzeń po dacie
  const groupEventsByDate = (events) => {
    return events.reduce((acc, event) => {
      const dateKey = event.date;
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(event);
      return acc;
    }, {});
  };

  const groupedPastEvents = groupEventsByDate(pastEvents);
  const groupedUpcomingEvents = groupEventsByDate(upcomingEvents);

  // Funkcja formatująca datę i dzień tygodnia
  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr);
    const dayName = date.toLocaleDateString('pl-PL', { weekday: 'long' });
    const formattedDate = date.toLocaleDateString('pl-PL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    const capitalizedDayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
    return `${capitalizedDayName}, ${formattedDate}`;
  };

  const renderEventsList = (groupedEvents) => (
    Object.keys(groupedEvents).map((dateKey) => (
      <Box key={dateKey}>
        <Box
          sx={{
            backgroundColor: 'rgba(76, 29, 149, 0.6)',
            padding: '8px 16px',
            borderRadius: '4px',
            marginBottom: '8px',
            marginTop: '12px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '3px solid rgb(196, 181, 253)'
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              color: 'white',
              fontWeight: '600',
              fontSize: '0.9rem'
            }}
          >
            {formatDateHeader(dateKey)}
          </Typography>
        </Box>

        {groupedEvents[dateKey].map((event) => (
          <ListItem
            key={event.id}
            sx={styles.mobileListItem}
            onClick={() => onEventClick(event)}
            button
          >
            <Box sx={{ width: '100%' }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 0.5
              }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '0.95rem'
                  }}
                >
                  {event.isMultiDayCopy ? `${event.name} (Dzień ${event.dayNumber})` : event.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: event.rank ? TOURNAMENT_RANKS[event.rank].color : 'rgb(196, 181, 253)',
                    fontSize: '0.7rem',
                    backgroundColor: event.rank ? `${TOURNAMENT_RANKS[event.rank].color}20` : 'rgba(76, 29, 149, 0.3)',
                    borderRadius: '4px',
                    padding: '2px 5px',
                    ml: 1,
                    whiteSpace: 'nowrap',
                    border: event.rank ? `1px solid ${TOURNAMENT_RANKS[event.rank].color}` : 'none',
                  }}
                >
                  {event.rank ? TOURNAMENT_RANKS[event.rank].name : 'Nieokreślona'}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgb(196, 181, 253)',
                  fontSize: '0.8rem',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {event.city} - {event.customLocation ? event.customLocation.name : event.organizer?.name || 'Nieznany organizator'}
              </Typography>
            </Box>
          </ListItem>
        ))}
      </Box>
    ))
  );

  return (
    <List sx={styles.mobileList}>
      {Object.keys(groupedPastEvents).length > 0 && (
        <>
          <Box
            sx={{
              backgroundColor: 'rgba(76, 29, 149, 0.6)',
              padding: '8px 16px',
              borderRadius: '4px',
              marginBottom: '8px',
              marginTop: '12px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderLeft: '3px solid rgb(196, 181, 253)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              transition: 'all 1s ease-in-out',
              '&:hover': {
                backgroundColor: 'rgba(76, 29, 149, 0.7)',
              }
            }}
            onClick={() => setShowPastEvents(!showPastEvents)}
          >
            <Typography
              variant="subtitle1"
              sx={{
                color: 'white',
                fontWeight: '600',
                fontSize: '0.9rem'
              }}
            >
              Zakończone wydarzenia
            </Typography>
            <IconButton
              size="small"
              sx={{
                color: 'white',
                transition: 'transform 1s ease-in-out',
                transform: showPastEvents ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
          <StyledCollapse in={showPastEvents} timeout={1000}>
            {renderEventsList(groupedPastEvents)}
          </StyledCollapse>
        </>
      )}

      {Object.keys(groupedUpcomingEvents).length > 0 ? (
        renderEventsList(groupedUpcomingEvents)
      ) : (
        <ListItem sx={styles.mobileListItem}>
          <Typography sx={{ color: 'rgb(196, 181, 253)', textAlign: 'center', width: '100%' }}>
            Brak nadchodzących wydarzeń
          </Typography>
        </ListItem>
      )}
    </List>
  );
};

export default MobileEventsList;